import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  BarChart2,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Plus,
  ClipboardList,
  Target,
  CreditCard,
  Eye,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import ADMIN_PATHS from "../ADMIN_PATHS";
import API from "../../../util/API";
import { convertAmount, normalizeCurrencyRates } from "../../../util/money";
import {
  SYSTEM_DEFAULT_CURRENCY_CHANGED_EVENT,
  fetchSystemDefaultCurrency,
  normalizeCurrencyCode,
} from "../../../util/systemCurrency";

const ACCENT = "#D2042D";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const fmt = {
  num: (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat().format(n);
  },
  money: (v, currency = "USD") => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${currency} ${fmt.num(n)}`;
    }
  },
  pct: (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(1)}%`;
  },
};

const tone = {
  good: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
    dot: "bg-emerald-500",
  },
  warn: {
    badge: "bg-amber-50 text-amber-700 border-amber-100",
    dot: "bg-amber-500",
  },
  bad: {
    badge: "bg-rose-50 text-rose-700 border-rose-100",
    dot: "bg-rose-500",
  },
  neutral: {
    badge: "bg-slate-50 text-slate-700 border-slate-100",
    dot: "bg-slate-400",
  },
};

const Card = ({ children, className }) => (
  <div className={cx("rounded-md border border-slate-900/15 bg-white", className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full" style={{ background: ACCENT }} />
        <h2 className="text-sm md:text-base font-semibold tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? (
        <p className="mt-1 text-xs text-slate-500 line-clamp-2">{subtitle}</p>
      ) : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const MetricTile = ({ title, value, change, trend = "up", icon: Icon, hint }) => (
  <Card className="p-4 md:p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          {title}
        </p>
        <p className="mt-2 text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900">
          {value}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span
            className={cx(
              "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold",
              trend === "up" ? tone.good.badge : tone.bad.badge
            )}
          >
            {trend === "up" ? (
              <ArrowUpRight size={14} />
            ) : (
              <ArrowDownRight size={14} />
            )}
            {change}
          </span>
          {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
        </div>
      </div>

      <div className="h-10 w-10 rounded-md border border-slate-900/15 bg-white flex items-center justify-center">
        <Icon size={18} className="text-slate-700" />
      </div>
    </div>
  </Card>
);

const FinanceTile = ({ title, value, icon: Icon, hint }) => (
  <Card className="p-4 md:p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          {title}
        </p>
        <p className="mt-2 text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900">
          {value}
        </p>
        {hint ? <p className="mt-2 text-xs text-slate-500">{hint}</p> : null}
      </div>

      <div className="h-10 w-10 rounded-md border border-slate-900/15 bg-white flex items-center justify-center">
        <Icon size={18} className="text-slate-700" />
      </div>
    </div>
  </Card>
);

const QuickAction = ({ title, description, icon: Icon, to }) => (
  <Link
    to={to}
    className="group block rounded-md border border-slate-900/15 bg-white p-4 md:p-5 transition-colors hover:border-slate-900/30 hover:bg-slate-50"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <p className="mt-1 text-xs text-slate-500 line-clamp-2">{description}</p>
      </div>
      <div className="h-10 w-10 rounded-md border border-slate-900/15 bg-white flex items-center justify-center group-hover:border-slate-900/30">
        <Icon size={18} className="text-slate-700" />
      </div>
    </div>

    <div className="mt-4 flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        Open
      </span>
      <span className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center group-hover:bg-black transition-colors">
        <ChevronRight size={16} />
      </span>
    </div>
  </Link>
);

const MiniRow = ({ label, value, toneKey = "neutral" }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="flex items-center gap-2 min-w-0">
      <span className={cx("h-2 w-2 rounded-full", tone[toneKey].dot)} />
      <span className="text-sm text-slate-600 truncate">{label}</span>
    </div>
    <span className="text-sm font-semibold text-slate-900">{value}</span>
  </div>
);

const Skeleton = ({ className }) => (
  <div className={cx("animate-pulse rounded-md bg-slate-100", className)} />
);

const formatShortDate = (value) => {
  if (!value) return "";
  const safe = new Date(`${value}T00:00:00`);
  if (Number.isNaN(safe.getTime())) return value;
  return safe.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [systemDefaultCurrency, setSystemDefaultCurrency] = useState("");
  const currencyPayload = useSelector((state) => state.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);

  const getdata = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await API.get("/extra/dashboard");
      setDashboardData(res?.data?.data || {});
      setLastUpdated(new Date());
    } catch (e) {
      setError("Failed to load dashboard. Please refresh.");
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSystemCurrency = async () => {
      try {
        const code = await fetchSystemDefaultCurrency();
        if (mounted) setSystemDefaultCurrency(code);
      } catch {
        if (mounted) setSystemDefaultCurrency("");
      }
    };

    loadSystemCurrency();

    const onSystemCurrencyChanged = (event) => {
      const code = normalizeCurrencyCode(event?.detail?.currency);
      if (code) setSystemDefaultCurrency(code);
    };

    window.addEventListener(SYSTEM_DEFAULT_CURRENCY_CHANGED_EVENT, onSystemCurrencyChanged);
    return () => {
      mounted = false;
      window.removeEventListener(SYSTEM_DEFAULT_CURRENCY_CHANGED_EVENT, onSystemCurrencyChanged);
    };
  }, []);

  // You can map real fields from your API here.
  // Keep these safe with defaults to avoid broken UI.
  const model = useMemo(() => {
    const d = dashboardData || {};

    const totalOrders = Number(d.totalOrders ?? 0);
    const totalProducts = Number(d.totalProducts ?? 0);
    const totalUsers = Number(d.totalUsers ?? 0);

    // If your backend later provides these, just replace:
    const totalRevenue = d.totalRevenue ?? d.totalSales ?? null; // number
    const currency = d.currency || "USD";
    const salesOverview = Array.isArray(d.salesOverview) ? d.salesOverview : [];
    const pageViewSeries = Array.isArray(d.pageViewSeries) ? d.pageViewSeries : [];
    const todayOrders = Number(d.todayOrders ?? 0);
    const todayRevenue = Number(d.todayRevenue ?? 0);
    const totalSales = d.totalSales ?? null;
    const totalEarnings = d.totalEarnings ?? null;
    const commissionEarnings = d.commissionEarnings ?? null;
    const pendingOrders = Number(d.pendingOrders ?? 0);
    const unresolvedPaidOrders = Number(d.unresolvedPaidOrders ?? 0);
    const estimatedPaidOrders = Number(d.estimatedPaidOrders ?? 0);

    // Example “change” values: replace with real deltas when your API includes them.
    const deltas = {
      revenue: d.deltaRevenuePct ?? 12.5,
      orders: d.deltaOrdersPct ?? 8.2,
      products: d.deltaProductsPct ?? 15.3,
      users: d.deltaUsersPct ?? 5.7,
      conversion: d.conversionRate ?? 3.2,
      conversionDelta: d.deltaConversionPct ?? -0.8,
      aov: d.avgOrderValue ?? null,
      aovDelta: d.deltaAovPct ?? 2.1,
      pageViews: d.pageViews ?? 0,
      uniqueVisitors: d.uniqueVisitors ?? 0,
    };

    const orders = {
      pending: d.ordersPending ?? 0,
      processing: d.ordersProcessing ?? 0,
      shipped: d.ordersShipped ?? 0,
      delivered: d.ordersDelivered ?? 0,
      cancelled: d.ordersCancelled ?? 0,
      refunded: d.ordersRefunded ?? 0,
    };

    const system = {
      apiCalls: d.apiCallsToday ?? 0,
      failureRate: d.failureRatePct ?? 0,
      uptime: d.uptimePct ?? 0,
      lastActivityAt: d.lastActivityAt ?? null,
    };

    const activity = d.recentActivity ?? [];

    return {
      currency,
      totals: { totalOrders, totalProducts, totalUsers, totalRevenue },
      salesOverview,
      pageViewSeries,
      todayOrders,
      todayRevenue,
      totalSales,
      totalEarnings,
      commissionEarnings,
      pendingOrders,
      unresolvedPaidOrders,
      estimatedPaidOrders,
      deltas,
      orders,
      system,
      activity,
    };
  }, [dashboardData]);

  const displayCurrency = useMemo(
    () =>
      normalizeCurrencyCode(systemDefaultCurrency) ||
      normalizeCurrencyCode(model.currency) ||
      "USD",
    [model.currency, systemDefaultCurrency]
  );

  const convertToDisplayCurrency = useCallback(
    (value, fromCurrency = model.currency) => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return Number.NaN;

      const sourceCurrency = normalizeCurrencyCode(fromCurrency) || "USD";
      const normalized = convertAmount(amount, {
        from: sourceCurrency,
        to: displayCurrency,
        rates,
      });
      return Number.isFinite(normalized) ? normalized : amount;
    },
    [displayCurrency, model.currency, rates]
  );

  const formatDisplayMoney = useCallback(
    (value, fromCurrency = model.currency) => {
      const converted = convertToDisplayCurrency(value, fromCurrency);
      if (!Number.isFinite(converted)) return "—";
      return fmt.money(converted, displayCurrency);
    },
    [convertToDisplayCurrency, displayCurrency, model.currency]
  );

  const salesSeries = useMemo(() => {
    const series = model.salesOverview || [];
    return series.map((item) => ({
      ...item,
      revenue: convertToDisplayCurrency(item?.revenue, model.currency),
      label: formatShortDate(item.date)
    }));
  }, [convertToDisplayCurrency, model.currency, model.salesOverview]);

  const trafficSeries = useMemo(() => {
    const series = model.pageViewSeries || [];
    return series.map((item) => ({
      ...item,
      label: formatShortDate(item.date)
    }));
  }, [model.pageViewSeries]);

  const primary = useMemo(
    () => [
      {
        title: "Total Revenue",
        value: formatDisplayMoney(model.totals.totalRevenue, model.currency),
        change: fmt.pct(model.deltas.revenue),
        trend: model.deltas.revenue >= 0 ? "up" : "down",
        icon: DollarSign,
        hint: "All channels",
      },
      {
        title: "Total Orders",
        value: fmt.num(model.totals.totalOrders),
        change: fmt.pct(model.deltas.orders),
        trend: model.deltas.orders >= 0 ? "up" : "down",
        icon: ShoppingCart,
        hint: "Lifetime",
      },
      {
        title: "Total Products",
        value: fmt.num(model.totals.totalProducts),
        change: fmt.pct(model.deltas.products),
        trend: model.deltas.products >= 0 ? "up" : "down",
        icon: Package,
        hint: "Listed",
      },
      {
        title: "Total Users",
        value: fmt.num(model.totals.totalUsers),
        change: fmt.pct(model.deltas.users),
        trend: model.deltas.users >= 0 ? "up" : "down",
        icon: Users,
        hint: "Registered",
      },
    ],
    [formatDisplayMoney, model]
  );

  const financeCards = useMemo(
    () => [
      {
        title: "Total Sales",
        value: formatDisplayMoney(model.totalSales, model.currency),
        icon: DollarSign,
        hint: "All time",
      },
      {
        title: "Total Earnings",
        value: formatDisplayMoney(model.totalEarnings, model.currency),
        icon: CreditCard,
        hint: "All time",
      },
      {
        title: "Pending Orders",
        value: fmt.num(model.pendingOrders),
        icon: ClipboardList,
        hint: "Awaiting approval",
      },
      {
        title: "Commission Earned",
        value: formatDisplayMoney(model.commissionEarnings, model.currency),
        icon: Target,
        hint: "All time",
      },
    ],
    [formatDisplayMoney, model]
  );

  const secondary = useMemo(
    () => [
      {
        title: "Conversion Rate",
        value: `${Number(model.deltas.conversion).toFixed(1)}%`,
        change: fmt.pct(model.deltas.conversionDelta),
        trend: model.deltas.conversionDelta >= 0 ? "up" : "down",
        icon: Target,
        hint: "Storefront",
      },
      {
        title: "Avg Order Value",
        value:
          model.deltas.aov == null
            ? "—"
            : formatDisplayMoney(model.deltas.aov, model.currency),
        change: fmt.pct(model.deltas.aovDelta),
        trend: model.deltas.aovDelta >= 0 ? "up" : "down",
        icon: CreditCard,
        hint: "Checkout",
      },
      {
        title: "Page Views",
        value: fmt.num(model.deltas.pageViews),
        change: "",
        trend: "up",
        icon: Eye,
        hint: "Last 7 days",
      },
      {
        title: "Unique Visitors",
        value: fmt.num(model.deltas.uniqueVisitors),
        change: "",
        trend: "up",
        icon: Users,
        hint: "Last 7 days",
      },
    ],
    [formatDisplayMoney, model]
  );

  const orderKpis = useMemo(() => {
    const o = model.orders;
    return [
      { label: "Pending", value: fmt.num(o.pending), toneKey: o.pending > 40 ? "warn" : "neutral" },
      { label: "Processing", value: fmt.num(o.processing), toneKey: o.processing > 30 ? "warn" : "neutral" },
      { label: "Shipped", value: fmt.num(o.shipped), toneKey: "good" },
      { label: "Delivered", value: fmt.num(o.delivered), toneKey: "good" },
      { label: "Cancelled", value: fmt.num(o.cancelled), toneKey: o.cancelled > 10 ? "bad" : "neutral" },
      { label: "Refunded", value: fmt.num(o.refunded), toneKey: o.refunded > 5 ? "bad" : "neutral" },
    ];
  }, [model.orders]);

  const systemTone = useMemo(() => {
    const failureRate = Number(model.system.failureRate);
    const up = Number(model.system.uptime);
    const failureTone = failureRate >= 5 ? "bad" : failureRate >= 2 ? "warn" : "good";
    const upTone = up < 99 ? "warn" : "good";
    return { failureTone, upTone };
  }, [model.system]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <h1
              className="text-[1.9rem] leading-[1.03] text-slate-900"
              style={{ fontFamily: '"Iowan Old Style", "Times New Roman", serif' }}
            >
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Store pulse, operational health, and quick actions in one place.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                <Clock size={14} className="text-slate-500" />
                {lastUpdated ? (
                  <span>
                    Updated{" "}
                    <span className="font-semibold text-slate-900">
                      {lastUpdated.toLocaleTimeString()}
                    </span>
                  </span>
                ) : (
                  <span>Not updated yet</span>
                )}
              </span>

              {error ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                  <AlertTriangle size={14} />
                  {error}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                  <CheckCircle2 size={14} />
                  System normal
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={getdata}
              disabled={loading}
              className={cx(
                "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold border transition-colors",
                loading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <Link
              to={ADMIN_PATHS.PRODUCTS?.ALIEXPRESS || "/administrator/products/addProduct/aliexpress"}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-black transition-colors"
            >
              <Plus size={16} />
              Add Product
            </Link>
          </div>
        </div>

        {model.unresolvedPaidOrders > 0 ? (
          <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {model.unresolvedPaidOrders} paid legacy order(s) are excluded from exact USD finance totals because their historical exchange rate was not stored.
          </div>
        ) : null}
        {model.estimatedPaidOrders > 0 ? (
          <div className="mb-6 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            {model.estimatedPaidOrders} legacy paid order(s) use the current exchange rate for USD reporting.
          </div>
        ) : null}
        {/* Primary Metrics */}
        <Card className="p-4 md:p-5 mb-6 md:mb-8">
          <SectionHead
            title="Finance Overview"
            subtitle="Revenue, earnings, and pending orders from live data."
            right={
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                All time
              </span>
            }
          />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            {financeCards.map((card) => (
              <FinanceTile key={card.title} {...card} />
            ))}
          </div>
        </Card>

        {/* Primary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          {loading && !dashboardData ? (
            <>
              <Skeleton className="h-[132px]" />
              <Skeleton className="h-[132px]" />
              <Skeleton className="h-[132px]" />
              <Skeleton className="h-[132px]" />
            </>
          ) : (
            primary.map((m) => <MetricTile key={m.title} {...m} />)
          )}
        </div>

        {/* Sales Overview + Quick Actions */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-5 mb-6 md:mb-8">
          {/* Sales Overview */}
          <Card className="xl:col-span-7 p-4 md:p-5">
            <SectionHead
              title="Sales Overview"
              subtitle="Daily sales totals for the last 7 days."
              right={
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Last 7 days
                </span>
              }
            />

            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    Today Orders
                  </p>
                  <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                    {fmt.num(model.todayOrders)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Today revenue: {formatDisplayMoney(model.todayRevenue, model.currency)}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                    <BarChart2 size={14} className="text-slate-500" />
                    Trend view
                  </span>
                </div>
              </div>

              <div className="mt-4 h-44 md:h-52 rounded-md border border-slate-200 bg-white">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesSeries} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                    <Tooltip
                      formatter={(value) => [formatDisplayMoney(value, displayCurrency), "Revenue"]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={ACCENT}
                      fill={`${ACCENT}22`}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="xl:col-span-5 p-4 md:p-5">
            <SectionHead
              title="Quick Actions"
              subtitle="Daily operations in one place."
            />

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <QuickAction
                title="Add Product"
                description="Create a new product manually"
                icon={Plus}
                to={ADMIN_PATHS.PRODUCTS?.ALIEXPRESS || "/administrator/products/addProduct/aliexpress"}
              />
              <QuickAction
                title="Import Products"
                description="Import from AliExpress"
                icon={Package}
                to={ADMIN_PATHS.PRODUCTS?.IMPORT || "/manage-admin/products/import"}
              />
              <QuickAction
                title="Order Management"
                description="View and manage order pipeline"
                icon={ClipboardList}
                to={ADMIN_PATHS.ORDERS || "/manage-admin/orders"}
              />
              <QuickAction
                title="User Management"
                description="Manage user accounts and roles"
                icon={Users}
                to={ADMIN_PATHS.USERS || "/manage-admin/users"}
              />
            </div>
          </Card>
        </div>

        {/* Secondary Metrics */}
        <Card className="p-4 md:p-5 mb-6 md:mb-8">
          <SectionHead
            title="Performance Metrics"
            subtitle="Front-of-store signals that influence revenue (conversion, AOV, traffic)."
          />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            {secondary.map((m) => (
              <MetricTile key={m.title} {...m} />
            ))}
          </div>
        </Card>

        {/* Traffic Overview */}
        <Card className="p-4 md:p-5 mb-6 md:mb-8">
          <SectionHead
            title="Traffic Overview"
            subtitle="Daily page views and unique visitors (last 7 days)."
            right={
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Last 7 days
              </span>
            }
          />

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#0EA5E9" }} />
                Page Views
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: ACCENT }} />
                Unique Visitors
              </span>
            </div>

            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trafficSeries} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <Tooltip
                    formatter={(value, name) => [fmt.num(value), name]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name="Page Views"
                    stroke="#0EA5E9"
                    fill="rgba(14, 165, 233, 0.15)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="uniqueVisitors"
                    name="Unique Visitors"
                    stroke={ACCENT}
                    fill={`${ACCENT}22`}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Order Status + System Health */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-5 mb-6 md:mb-8">
          {/* Order Status */}
          <Card className="xl:col-span-7 p-4 md:p-5">
            <SectionHead
              title="Order Status"
              subtitle="Operational view of the pipeline. Watch pending and cancellations."
            />

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              {orderKpis.map((k) => (
                <div
                  key={k.label}
                  className="rounded-md border border-slate-200 bg-white p-4 flex items-center justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {k.label}
                    </p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {k.value}
                    </p>
                  </div>
                  <span
                    className={cx(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                      tone[k.toneKey].badge
                    )}
                  >
                    <span className={cx("h-2 w-2 rounded-full", tone[k.toneKey].dot)} />
                    {k.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 h-px bg-slate-200" />
          </Card>

          {/* System Health */}
          <Card className="xl:col-span-5 p-4 md:p-5">
            <SectionHead
              title="System Health"
              subtitle="Keep reliability visible. Stable ops protects conversion."
            />

            <div className="mt-4 divide-y divide-slate-100">
              <MiniRow label="API Calls Today" value={fmt.num(model.system.apiCalls)} toneKey="neutral" />
              <MiniRow
                label="Failure Rate"
                value={`${Number(model.system.failureRate).toFixed(1)}%`}
                toneKey={systemTone.failureTone}
              />
              <MiniRow
                label="Uptime"
                value={`${Number(model.system.uptime).toFixed(1)}%`}
                toneKey={systemTone.upTone}
              />
              <MiniRow
                label="Last Activity"
                value={model.system.lastActivityAt ? new Date(model.system.lastActivityAt).toLocaleString() : "—"}
                toneKey="neutral"
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Alerts
                </p>
                <p className="mt-1 text-xl font-semibold text-slate-900">0</p>
                <p className="mt-1 text-xs text-slate-500">No incidents detected</p>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Mode
                </p>
                <p className="mt-1 text-xl font-semibold text-slate-900">Live</p>
                <p className="mt-1 text-xs text-slate-500">Realtime monitoring</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="p-4 md:p-5">
          <SectionHead
            title="Recent Activity"
            subtitle="A slim audit timeline. Later you can turn this into a full event log."
          />

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-3">
            {(model.activity || []).slice(0, 8).map((a, idx) => {
              const kind = a.type || "system";
              const dotTone =
                kind === "order"
                  ? "good"
                  : kind === "refund"
                  ? "bad"
                  : kind === "product"
                  ? "neutral"
                  : kind === "user"
                  ? "warn"
                  : "neutral";

              return (
                <div
                  key={`${idx}-${a.time}`}
                  className="rounded-md border border-slate-200 bg-white p-4 flex items-start gap-3"
                >
                  <span className={cx("mt-1 h-2.5 w-2.5 rounded-full", tone[dotTone].dot)} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 line-clamp-2">{a.message}</p>
                    <p className="mt-1 text-xs text-slate-500">{a.time}</p>
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                    {kind}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
